<template>
  <form @submit.prevent novalidate="true">
    <b-modal ref="checkReportFileNamePreview" size="lg" scrollable ok-only
             :title="$t('Webportal.Dialog.CheckReportFileNamePreview.Title')"
             :ok-title="$t('Webportal.ClosePreview')">
      <pre>{{ checkReportFileNameSamples }}</pre>
    </b-modal>
    <b-modal ref="checkUploadFileNamePreview" size="lg" scrollable ok-only
             :title="$t('Webportal.Dialog.CheckUploadFileNamePreview.Title')"
             :ok-title="$t('Webportal.ClosePreview')">
      <pre>{{ checkUploadFileNameSamples }}</pre>
    </b-modal>

    <b-card>
      <!-- Client selection -->
      <template slot="header">
        <div class="form-group form-row mb-0">
          <div class="col-lg-6">
            <treeselect :value="clientValue"
                        :clearable="false"
                        :placeholder="$t('Webportal.ClientFilter.Placeholder')"
                        :options="clientItems"
                        :disableFuzzyMatching="true"
                        :disabled="isBusy"
                        @input="clientInput">
            </treeselect>
          </div>
          <div class="col-1 ml-auto" v-bind:class="{ inlineSpinner: isBusy }"></div>
        </div>
      </template>

      <div v-if="clientSettings && themes" class="d-flex flex-column">
        <collapsible-panel class="form-group" :value="true" accordion="clientSettings" :header="$t('Webportal.Administration.Appearance')" header-class="h5">
          <!-- Logo -->
          <div class="form-group form-row">
            <label for="Logo" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.Logo') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.Logo.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <image-uploader ref="logoUploader"
                              :value="getLogoUrl()"
                              @input="setLogoUrl($event)"
                              :upload-url="`api/administration/client-logo?client-id=${clientSettings.ClientId}`"
                              :previewHeight="60"
                              v-validate="'required'"
                              data-vv-name="Logo" />
            </div>
          </div>

          <div class="form-group form-row">
            <label for="UseLogoForNotification" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.UseLogoForNotification') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.UseLogoForNotification.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.UseLogoForNotification"
                      v-validate data-vv-name="UseLogoForNotification"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- ThemeMode -->
          <div class="form-group form-row">
            <label for="ThemeMode" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ThemeMode') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ThemeMode.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="ThemeMode"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="themeMode"
                                  :disabled="isBusy"
                                  @change="themeModeChanged">
                <b-form-radio :value="1">{{ $t('Webportal.Administration.ThemeMode.Color') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ThemeMode.Color.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="2">{{ $t('Webportal.Administration.ThemeMode.CI') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ThemeMode.CI.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </div>

          <div class="form-group form-row" v-show="themeMode == ThemeMode.Color">
            <label for="ThemeMainColor" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ThemeMainColor') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ThemeMainColor.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-dropdown id="ThemeMainColor" size="sm" variant="outline-secondary" menu-class="color-dropdown"
                          :disabled="isBusy">
                <template v-slot:button-content>
                  <div class="color-dropdown-item" :style="{ 'background-color': mainColor }">&nbsp;</div>
                </template>
                <b-dropdown-item v-for="color in mainColors" :key="color"
                                @click="mainColorChanged(color)">
                  <div class="color-dropdown-item" :style="{ 'background-color': color }">&nbsp;</div>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="Theme" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.Theme') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.Theme.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-dropdown id="Theme" size="sm" class="d-block" variant="outline-secondary" menu-class="color-picker"
                          :disabled="isBusy || filteredThemes.length == 0" v-validate="'required'" data-vv-name="Theme" data-vv-value-path="selectedTheme">
                <template v-slot:button-content>
                  <div class="color-dropdown-item" :style="{ 'background-color': selectedTheme ? selectedTheme.Color1 : null }">&nbsp;</div>
                  <div class="color-dropdown-item" :style="{ 'background-color': selectedTheme ? selectedTheme.Color2 : null }">&nbsp;</div>
                  <div v-show="themeMode == ThemeMode.CI" class="color-dropdown-item" :style="{ 'background-color': selectedTheme ? selectedTheme.Color3 : null }">&nbsp;</div>
                  <b-badge pill style="min-width:30px;">{{ selectedTheme ? (selectedTheme.ThemeModeId == ThemeMode.Color ? selectedTheme.Variant : selectedTheme.Name) : null }}</b-badge>
                </template>
                <b-dropdown-header>
                  <div class="color-dropdown-item text-center">{{ $t('Webportal.Administration.Theme.Color1') }}</div>
                  <div class="color-dropdown-item text-center">{{ $t('Webportal.Administration.Theme.Color2') }}</div>
                  <div v-show="themeMode == ThemeMode.CI" class="color-dropdown-item text-center">{{ $t('Webportal.Administration.Theme.Color3') }}</div>
                </b-dropdown-header>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item v-for="theme in filteredThemes" :key="theme.ThemeId"
                                @click="themeChanged(theme)">
                  <div class="color-dropdown-item" :style="{ 'background-color': theme.Color1 }">&nbsp;</div>
                  <div class="color-dropdown-item" :style="{ 'background-color': theme.Color2 }">&nbsp;</div>
                  <div v-show="themeMode == ThemeMode.CI" class="color-dropdown-item" :style="{ 'background-color': theme.Color3 }">&nbsp;</div>
                  <b-badge pill style="min-width:30px;">{{ theme.ThemeModeId == ThemeMode.Color ? theme.Variant : theme.Name }}</b-badge>
                </b-dropdown-item>
              </b-dropdown>

              <b-alert class="mt-3 mb-0" :show="noDataAlert" variant="warning" dismissible
                      @dismissed="noDataAlert=0">
                {{ $t('Webportal.NoDataFound') }}
              </b-alert>

              <b-button class="mt-3" variant="secondary" size="sm"
                        :disabled="isBusy"
                        @click="showCheckReportPreview()">
                <font-awesome-icon icon="vial" /> {{ $t('Webportal.ShowPreview') }}
              </b-button>
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel class="form-group" accordion="clientSettings" :header="$t('Webportal.Administration.ScoreCalculation')" header-class="h5">
          <!-- Count of decimal places -->
          <div class="form-group form-row">
            <label for="PercentageNumberOfDecimalPoints" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.PercentageNumberOfDecimalPoints') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.PercentageNumberOfDecimalPoints.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="PercentageNumberOfDecimalPoints"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="clientSettings.PercentageNumberOfDecimalPoints"
                                  :options="percentageNumbersOptions"
                                  :disabled="isBusy"
                                  v-validate="'required'"
                                  data-vv-name="PercentageNumberOfDecimalPoints" />
            </div>
          </div>

          <!-- Overall calculation type -->
          <div class="form-group form-row">
            <label for="OverallCalculationType" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.OverallCalculationType') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="OverallCalculationType"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="clientSettings.OverallCalculationType"
                                  :disabled="isBusy"
                                  v-validate="'required'"
                                  data-vv-name="OverallCalculationType">
                <b-form-radio :value="0">{{ $t('Webportal.Administration.OverallCalculationType.Standard') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Standard.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="1">{{ $t('Webportal.Administration.OverallCalculationType.Weighted') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Weighted.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="2">{{ $t('Webportal.Administration.OverallCalculationType.Average') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Average.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowOverallCalculationTypeDescription" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowOverallCalculationTypeDescription') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowOverallCalculationTypeDescription.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowOverallCalculationTypeDescription"
                      v-validate data-vv-name="ShowOverallCalculationTypeDescription"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- Overall dynamic weighting -->
          <div class="form-group form-row">
            <label for="OverallCalculationDynamicWeighting" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.OverallCalculationDynamicWeighting') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationDynamicWeighting.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="OverallCalculationDynamicWeighting"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="clientSettings.OverallCalculationDynamicWeighting"
                                  :disabled="isBusy || clientSettings.OverallCalculationType !== 1"
                                  v-validate="'required'"
                                  data-vv-name="OverallCalculationDynamicWeighting">
                <b-form-radio :value="0">{{ $t('Webportal.Administration.OverallCalculationDynamicWeighting.Off') }}</b-form-radio>
                <b-form-radio :value="1">{{ $t('Webportal.Administration.OverallCalculationDynamicWeighting.Factor') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationDynamicWeighting.Factor.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="2">{{ $t('Webportal.Administration.OverallCalculationDynamicWeighting.Percent') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationDynamicWeighting.Percent.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel class="form-group" accordion="clientSettings" :header="$t('Webportal.Administration.ScoreThresholds')"
          :headerDescription="$t('Webportal.Administration.ScoreThresholds.Description')" header-class="h5">
          <div class="form-group form-row">
            <div class="col">
              <threshold-selector 
                v-model="surveyThresholds"
                v-validate="'required'"
                data-vv-name="Thresholds" 
              />
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel class="form-group" accordion="clientSettings" :header="$t('Webportal.Administration.GeneralSettings')" header-class="h5">
          <div class="form-group form-row">
            <label for="SurveyPreselection" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyPreselection') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyPreselection.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <treeselect v-model="clientSettings.SurveyPreselection"
                          v-validate data-vv-name="SurveyPreselection"
                          :multiple="true"
                          :defaultExpandLevel="Infinity"
                          :placeholder="$t('Webportal.SurveyFilter.Placeholder')"
                          :options="surveyItems"
                          :disableFuzzyMatching="true"
                          :disable-branch-nodes="true"
                          :disabled="isBusy">
              </treeselect>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="DateRangeFilterMode" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.DateRangeFilterMode') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.DateRangeFilterMode.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="DateRangeFilterMode"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="clientSettings.DateRangeFilterMode"
                                  :disabled="isBusy"
                                  v-validate="'required'"
                                  data-vv-name="DateRangeFilterMode">
                <b-form-radio :value="0">{{ $t('Webportal.Administration.DateRangeFilterMode.Disabled') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.DateRangeFilterMode.Disabled.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="1">{{ $t('Webportal.Administration.DateRangeFilterMode.TimeStampSurvey') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.DateRangeFilterMode.TimeStampSurvey.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="2">{{ $t('Webportal.Administration.DateRangeFilterMode.CheckReportCreated') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.DateRangeFilterMode.CheckReportCreated.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </div>

          <hr />

          <!-- Default overall score in raw data -->
          <div class="form-group form-row">
            <label for="ShowStandardOverallScoreForRawDataCustomer" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowStandardOverallScoreForRawDataCustomer') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowStandardOverallScoreForRawDataCustomer.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowStandardOverallScoreForRawDataCustomer"
                      v-validate data-vv-name="ShowStandardOverallScoreForRawDataCustomer"
                      :disabled="isBusy || clientSettings.OverallCalculationType === 0">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- KPI Groups in raw data -->
          <div class="form-group form-row">
            <label for="ShowKpiGroupsForRawData" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowKpiGroupsForRawData') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowKpiGroupsForRawData.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowKpiGroupsForRawData"
                      v-validate data-vv-name="ShowKpiGroupsForRawData"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- Checkdate -->
          <div class="form-group form-row">
            <label for="ShowCheckDate" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckDate') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckDate.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowCheckDate"
                      v-validate data-vv-name="ShowCheckDate"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowCheckDateForRawData" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckDateForRawData') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckDateForRawData.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowCheckDateForRawData"
                      v-validate data-vv-name="ShowCheckDateForRawData"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- Checktime -->
          <div class="form-group form-row">
            <label for="ShowCheckTime" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckTime') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckTime.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowCheckTime"
                      v-validate data-vv-name="ShowCheckTime"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowCheckTimeForRawData" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckTimeForRawData') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckTimeForRawData.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowCheckTimeForRawData"
                      v-validate data-vv-name="ShowCheckTimeForRawData"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- Checkstatus -->
          <div class="form-group form-row">
            <label for="ShowCheckStatus" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckStatus') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckStatus.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowCheckStatus"
                      v-validate data-vv-name="ShowCheckStatus"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- ShowTargetGroupSelection -->
          <div class="form-group form-row">
            <label for="ShowTargetGroupSelection" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowTargetGroupSelection') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowTargetGroupSelection.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowTargetGroupSelection"
                      v-validate data-vv-name="ShowTargetGroupSelection"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="IsReleaseCheckEnabled" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ReleaseCheck') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ReleaseCheck.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.IsReleaseCheckEnabled"
                      v-validate data-vv-name="IsReleaseCheckEnabled"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ReleaseCheckCountryFilter" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ReleaseCheckCountryFilter') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ReleaseCheckCountryFilter.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <treeselect v-model="clientSettings.ReleaseCheckCountryFilter"
                :placeholder="$t('Webportal.CountryFilter.Placeholder')"
                :options="clientSettings.LocationCountries"
                :normalizer="(node) => ({ id: node.CountryId, label: node.CountryName })"
                :disableFuzzyMatching="true"
                :matchKeys="[ 'id', 'label' ]"
                :multiple="true"
                v-validate data-vv-name="ReleaseCheckCountryFilter"
                :disabled="isBusy || !clientSettings.IsReleaseCheckEnabled">
                <template #option-label="{ node }">
                  <span :class="['flag-icon', `flag-icon-${node.id.toLowerCase()}`]"></span> {{ node.id }} - {{ node.label }}
                </template>
                <template #value-label="{ node }">
                  <span :class="['flag-icon', `flag-icon-${node.id.toLowerCase()}`]"></span> {{ node.id }} - {{ node.label }}
                </template>                
              </treeselect>
            </div>
          </div>

          <hr />

          <!-- ClassificationLevel -->
          <div class="form-group form-row">
            <label for="ClassificationLevel" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ClassificationLevel') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ClassificationLevel.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="ClassificationLevel"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="clientSettings.ClassificationLevel"
                                  :disabled="isBusy"
                                  v-validate="'required'"
                                  data-vv-name="ClassificationLevel">
                <b-form-radio :value="0">{{ $t('Reporting.ClassificationLevel.None') }}</b-form-radio>
                <b-form-radio button-variant="outline-info" :value="1">{{ $t('Reporting.ClassificationLevel.Confidential') }}</b-form-radio>
                <b-form-radio button-variant="outline-warning" :value="2">{{ $t('Reporting.ClassificationLevel.Secret') }}</b-form-radio>
                <b-form-radio button-variant="outline-danger" :value="3">{{ $t('Reporting.ClassificationLevel.TopSecret') }}</b-form-radio>
              </b-form-radio-group>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="CheckReportNotificationDownloadLinkExpirationInDays" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.CheckReportNotificationDownloadLinkExpirationInDays') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.CheckReportNotificationDownloadLinkExpirationInDays.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <dx-number-box v-model="clientSettings.CheckReportNotificationDownloadLinkExpirationInDays"
                            class="custom-control-inline" width="6em" height="2em"
                            v-validate="'required'" data-vv-name="CheckReportNotificationDownloadLinkExpirationInDays" @input="void 0"
                            format="##0" :show-spin-buttons="true" :step="1" :min="1" :max="999"
                            :disabled="isBusy" />
              {{ clientSettings.CheckReportNotificationDownloadLinkExpirationInDays > 1 ? $t('Webportal.NotificationSettings.Days') : $t('Webportal.NotificationSettings.Day') }}
            </div>
          </div>

          <!-- CheckReportFileNameTemplate -->
          <div class="form-group form-row">
            <label for="CheckReportFileNameTemplate" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.CheckReportFileNameTemplate') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.CheckReportFileNameTemplate.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <textarea class="form-control" rows="3"
                        v-model="clientSettings.CheckReportFileNameTemplate"
                        v-validate="{regex: /\{.+\}/ }" data-vv-name="CheckReportFileNameTemplate" />
              <b-button class="mt-3" variant="secondary" size="sm"
                        :disabled="isBusy ||
                                  clientSettings.CheckReportFileNameTemplate == '' ||
                                  clientSettings.CheckReportFileNameTemplate == null ||
                                  (validationFields['CheckReportFileNameTemplate'] && validationFields['CheckReportFileNameTemplate'].valid == false)"
                        @click="showCheckReportFileNamePreview()">
                <font-awesome-icon icon="vial" /> {{ $t('Webportal.ShowPreview') }}
              </b-button>
              <b-button class="mt-3" variant="secondary" size="sm"
                        @click="resetCheckReportFileNameTemplate()">
                <font-awesome-icon icon="sliders-h-square" /> {{ $t('Webportal.Administration.ResetToDefault') }}
              </b-button>
            </div>
          </div>

          <!-- CheckUploadFileNameTemplate -->
          <div class="form-group form-row">
            <label for="CheckUploadFileNameTemplate" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.CheckUploadFileNameTemplate') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.CheckUploadFileNameTemplate.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <textarea class="form-control" rows="3"
                        v-model="clientSettings.CheckUploadFileNameTemplate"
                        v-validate="{regex: /\{.+\}/ }" data-vv-name="CheckUploadFileNameTemplate" />
              <b-button class="mt-3" variant="secondary" size="sm"
                        :disabled="isBusy ||
                                  clientSettings.CheckUploadFileNameTemplate == '' ||
                                  clientSettings.CheckUploadFileNameTemplate == null ||
                                  (validationFields['CheckUploadFileNameTemplate'] && validationFields['CheckUploadFileNameTemplate'].valid == false)"
                        @click="showCheckUploadFileNamePreview()">
                <font-awesome-icon icon="vial" /> {{ $t('Webportal.ShowPreview') }}
              </b-button>
              <b-button class="mt-3" variant="secondary" size="sm"
                        @click="resetCheckUploadFileNameTemplate()">
                <font-awesome-icon icon="sliders-h-square" /> {{ $t('Webportal.Administration.ResetToDefault') }}
              </b-button>
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel class="form-group" accordion="clientSettings" :header="$t('Webportal.Dashboard')" header-class="h5">
          <div class="form-group form-row">
            <label for="ShowScoreThresholdsSelection" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowScoreThresholdsSelection') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowScoreThresholdsSelection.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowScoreThresholdsSelection"
                      v-validate data-vv-name="ShowScoreThresholdsSelection"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- EnableDashboardGrid -->
          <div class="form-group form-row">
            <label for="EnableDashboardGrid" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.EnableDashboardGrid') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.EnableDashboardGrid.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.DashboardTabs.EnableDashboardGrid"
                      v-validate data-vv-name="EnableDashboardGrid"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- EnableDashboardRanking -->
          <div class="form-group form-row">
            <label for="EnableDashboardRanking" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.EnableDashboardRanking') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.EnableDashboardRanking.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.DashboardTabs.EnableDashboardRanking"
                      v-validate data-vv-name="EnableDashboardRanking"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- EnableDashboardFunnel -->
          <div class="form-group form-row">
            <label for="EnableDashboardFunnel" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.EnableDashboardFunnel') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.EnableDashboardFunnel.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.DashboardTabs.EnableDashboardFunnel"
                      v-validate data-vv-name="EnableDashboardFunnel"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- EnableDashboardGeomap -->
          <!-- <div class="form-group form-row">
            <label for="EnableDashboardGeomap" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.EnableDashboardGeomap') }}
              <font-awesome-icon :icon="['fas', 'info-circle']"
                                v-b-tooltip.hover.nofade="toolTipDelay"
                                :title="$t('Webportal.Administration.EnableDashboardGeomap.Description')" />
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.DashboardTabs.EnableDashboardGeomap"
                      v-validate data-vv-name="EnableDashboardGeomap"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div> -->

          <!-- EnableDashboardScorecard -->
          <!-- <div class="form-group form-row">
            <label for="EnableDashboardScorecard" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.EnableDashboardScorecard') }}
              <font-awesome-icon :icon="['fas', 'info-circle']"
                                v-b-tooltip.hover.nofade="toolTipDelay"
                                :title="$t('Webportal.Administration.EnableDashboardScorecard.Description')" />
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.DashboardTabs.EnableDashboardScorecard"
                      v-validate data-vv-name="EnableDashboardScorecard"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div> -->
        </collapsible-panel>

        <collapsible-panel class="form-group" accordion="clientSettings" :header="$t('Webportal.Actions.SurveyRevision')" header-class="h5">
          <div class="form-group form-row">
            <label for="SurveyRevisionCreationLimitInDays" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyRevisionCreationLimitInDays') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyRevisionCreationLimitInDays.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <dx-number-box v-model="clientSettings.SurveyRevisionCreationLimitInDays"
                            class="custom-control-inline" width="6em" height="2em"
                            v-validate="'required'" data-vv-name="SurveyRevisionCreationLimitInDays" @input="void 0"
                            format="##0" :show-spin-buttons="true" :step="1" :min="1" :max="999"
                            :disabled="isBusy" />
              {{ clientSettings.SurveyRevisionCreationLimitInDays > 1 ? $t('Webportal.NotificationSettings.Days') : $t('Webportal.NotificationSettings.Day') }}
            </div>
          </div>

          <div class="form-group form-row">
            <label for="SurveyRevisionCreationLimitPerCheck" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyRevisionCreationLimitPerCheck') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyRevisionCreationLimitPerCheck.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <dx-number-box v-model="clientSettings.SurveyRevisionCreationLimitPerCheck"
                            class="custom-control-inline" width="7em" height="2em"
                            v-validate data-vv-name="SurveyRevisionCreationLimitPerCheck" @input="void 0"
                            format="##0" :show-spin-buttons="true" :show-clear-button="true" :step="1" :min="1" :max="999"
                            :disabled="isBusy" />
            </div>
          </div>

          <div class="form-group form-row">
            <label for="AllowedSurveyRevisionStatuses" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.AllowedSurveyRevisionStatuses') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.AllowedSurveyRevisionStatuses.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <treeselect v-model="clientSettings.AllowedSurveyRevisionStatuses"
                :placeholder="$t('Webportal.AllowedSurveyRevisionStatuses.Placeholder')"
                :options="getRevisionStatusOptions"
                :normalizer="(node) => ({ id: node.value, label: node.text })"
                :disableFuzzyMatching="true"
                :matchKeys="[ 'id', 'label' ]"
                :multiple="true"
                sort-value-by="INDEX"
                v-validate data-vv-name="AllowedSurveyRevisionStatuses"
                :disabled="isBusy">
                <template #option-label="{ node }">
                  <span style="display:inline-block; width:1.3em;">{{ node.id }}</span>- <font-awesome-icon fixed-width :icon="['far', getRevisionStatusIcon(node.id)]" /> {{ node.label }}
                </template>
                <template #value-label="{ node }">
                  <span>{{ node.id }}</span> - <font-awesome-icon fixed-width :icon="['far', getRevisionStatusIcon(node.id)]" /> {{ node.label }}
                </template>                
              </treeselect>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="SurveyRevisionReasons" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyRevisionReasons') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyRevisionReasons.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-tags v-model="clientSettings.SurveyRevisionReasons"
                :placeholder="$t('Webportal.Administration.SurveyRevisionReasons.Placeholder')"
                v-validate data-vv-name="SurveyRevisionReasons"
                :disabled="isBusy" />
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowSurveyRevisionProcessingNote" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowSurveyRevisionProcessingNote') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowSurveyRevisionProcessingNote.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.ShowSurveyRevisionProcessingNote"
                      v-validate data-vv-name="ShowSurveyRevisionProcessingNote"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="CanEveryoneDismissSurveyRevisions" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.CanEveryoneDismissSurveyRevisions') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.CanEveryoneDismissSurveyRevisions.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.CanEveryoneDismissSurveyRevisions"
                      v-validate data-vv-name="CanEveryoneDismissSurveyRevisions"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="AttachCheckReportToSurveyRevision" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.AttachCheckReportToSurveyRevision') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.AttachCheckReportToSurveyRevision.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.AttachCheckReportToSurveyRevision"
                      v-validate data-vv-name="AttachCheckReportToSurveyRevision"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="SurveyRevisionOnlyQuestionsWithScoring" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyRevisionOnlyQuestionsWithScoring') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyRevisionOnlyQuestionsWithScoring.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="clientSettings.SurveyRevisionOnlyQuestionsWithScoring"
                      @input="setSettingValue('SurveyRevisionOnlyQuestionsWithScoring', $event.target.checked); showSurveyRevisionOnlyQuestionsWithScoringChangeMsgBox();"
                      v-validate data-vv-name="SurveyRevisionOnlyQuestionsWithScoring"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="SurveyRevisionResultAdjustmentEnabled" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyRevisionResultAdjustmentEnabled') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyRevisionResultAdjustmentEnabled.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.SurveyRevisionResultAdjustmentEnabled"
                      v-validate data-vv-name="SurveyRevisionResultAdjustmentEnabled"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="AllowedSurveyRevisionResultAdjustmentOptions" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.AllowedSurveyRevisionResultAdjustmentOptions') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.AllowedSurveyRevisionResultAdjustmentOptions.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <treeselect v-model="clientSettings.AllowedSurveyRevisionResultAdjustmentOptions"
                :placeholder="$t('Webportal.AllowedSurveyRevisionResultAdjustmentOptions.Placeholder')"
                :options="resultAdjustmentOptions"
                :normalizer="(node) => ({ id: node.value, label: node.text })"
                :disableFuzzyMatching="true"
                :matchKeys="[ 'id', 'label' ]"
                :multiple="true"
                sort-value-by="INDEX"
                v-validate data-vv-name="AllowedSurveyRevisionResultAdjustmentOptions"
                :disabled="isBusy">
                <template #option-label="{ node }">
                  <span style="display:inline-block; width:1.0em;">{{ node.id }}</span>- <font-awesome-icon fixed-width :icon="['far', node.raw.resultAdjustment ? 'check-circle' : 'times-circle']" /> {{ node.label }}
                </template>
                <template #value-label="{ node }">
                  <span>{{ node.id }}</span> - <font-awesome-icon fixed-width :icon="['far', node.raw.resultAdjustment ? 'check-circle' : 'times-circle']" /> {{ node.label }}
                </template>
              </treeselect>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="GeneralInquiryAutoCloseInDays" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.GeneralInquiryAutoCloseInDays') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.GeneralInquiryAutoCloseInDays.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <dx-number-box v-model="clientSettings.GeneralInquiryAutoCloseInDays"
                            class="custom-control-inline" width="7em" height="2em"
                            v-validate data-vv-name="GeneralInquiryAutoCloseInDays" @input="void 0"
                            format="##0" :show-spin-buttons="true" :show-clear-button="true" :step="1" :min="1" :max="999"
                            :disabled="isBusy" />
              <template v-if="clientSettings.GeneralInquiryAutoCloseInDays">
                {{ clientSettings.GeneralInquiryAutoCloseInDays > 1 ? $t('Webportal.NotificationSettings.Days') : $t('Webportal.NotificationSettings.Day') }}
              </template>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="SurveyRevisionCustomFlag1Enabled" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyRevisionCustomFlag1Enabled') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyRevisionCustomFlag1Enabled.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="clientSettings.SurveyRevisionCustomFlag1Enabled"
                      v-validate data-vv-name="SurveyRevisionCustomFlag1Enabled"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel class="form-group" accordion="clientSettings" :header="$t('Webportal.DefaultNotifications')" header-class="h5">
          <notification-settings :clientId="clientSettings.ClientId" :disabled="isBusy"></notification-settings>
        </collapsible-panel>

        <collapsible-panel class="form-group" accordion="clientSettings" :header="$t('Webportal.Administration.SupportInformation')" header-class="h5">
          <div class="form-group form-row align-items-center">
            <label class="col-auto ml-auto">
              {{ $t('Webportal.UseGeneralSettings') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       :checked="useGeneralSetting('SupportInformation')"
                       @input="resetSettingValue('SupportInformation', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>
          <support-information :value="getSettingValue('SupportInformation')" 
            v-validate data-vv-name="SupportInformation" :disabled="clientSettings.SupportInformation == null"></support-information>
        </collapsible-panel>
      </div>

      <template slot="footer">
        <div class="float-right">
          <button type="button" @click="cancel" class="btn btn-secondary" :disabled="!isFormChanged || isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t('Webportal.Cancel') }}
          </button>
          <button type="button" @click="submit" class="btn btn-primary" :disabled="!isFormChanged || !isFormValid || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t('Webportal.Save') }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import SurveysService from '@/services/SurveysService';
import AdministationService from '@/services/AdministationService';
import EventBus from '@/services/EventBus.js';
import CollapsiblePanel from '@/components/CollapsiblePanel';
import NotificationSettings from './NotificationSettings';
import ThresholdSelector from './ThresholdSelector';
import ImageUploader from './ImageUploader';
import SupportInformation from './SupportInformation.vue';
import { DxNumberBox } from 'devextreme-vue';
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";
import { set } from 'lodash';

const ThemeMode = {
  Color: 1,
  CI: 2
}

export default {
  name: "clientSettings",

  provide() {
    return {
      parentValidator: this.$validator
    }
  },

  mixins: [ AppConfig, SurveyRevisionMixin ],

  components: {
    CollapsiblePanel,
    NotificationSettings,
    ThresholdSelector,
    ImageUploader,
    SupportInformation,
    DxNumberBox
  },

  data () {
    return {
      // Filter properties
      structureItems: [],                // Data items for client treeselect
      clientValue: null,                 // Selected client item
      clientSettings: null,

      ThemeMode: ThemeMode,
      themeMode: ThemeMode.Color,
      mainColor: null,
      themes: null,
      noDataAlert: 0,

      surveyThresholds: null,

      checkReportFileNameSamples: null,
      checkUploadFileNameSamples: null,

      isBusy: false,

      percentageNumbersOptions: [
        { text: '0', value: 0 },
        { text: '1', value: 1 },
        { text: '2', value: 2 }
      ],
    }
  },

  mounted() {
    this.initializeTheme();
  },

  watch: {
    clientSettings (newVal, oldVal) {
      this.surveyThresholds =
        newVal?.Thresholds?.map(x => { return { value: { startValue: x.Minimum, endValue: x.Maximum }, color: x.Color, icons: x.Icons }});
    },

    surveyThresholds: {
      handler: function(newVal, oldVal) {
        if (this.clientSettings == null)
          return;

        this.clientSettings.Thresholds = 
          newVal?.map(x => { return { ScoreId: -1, Minimum: x.value.startValue, Maximum: x.value.endValue, Color: x.color, Icons: x.icons }});
      }, deep: true
    }
  },

  computed: {
    isFormChanged() {
      return Object.keys(this.validationFields).some(key => this.validationFields[key].dirty || this.validationFields[key].changed);
    },

    isFormValid() {
      return Object.keys(this.validationFields).every(key => this.validationFields[key].valid === null || this.validationFields[key].valid || key === 'Logo');
    },

    mainColors() {
      return _(this.themes)
          .filter(t => t.ThemeModeId === ThemeMode.Color)
          .map(t => t.MainColor)
          .uniq()
          // Order colors by brightness
          .orderBy(color => {
            var r, g, b;

            color = +("0x" + color.slice(1).replace(color.length < 5 && /./g, '$&$&'));

            r = color >> 16;
            g = color >> 8 & 255;
            b = color & 255;

            return ((r * 299) + (g * 587) + (b * 114)) / 1000;
          }, 'desc')
          .value();
    },

    filteredThemes() {
      return this.themes.filter(t => t.ThemeModeId == this.themeMode && (this.themeMode == ThemeMode.CI || t.MainColor === this.mainColor));
    },

    selectedTheme() {
      let result = this.themes != null && this.clientSettings != null ?
          this.themes.find(t => t.ThemeId == this.clientSettings.ThemeId) : null;

      if (this.validationFields['Theme'] != null)
        this.validationFields['Theme'].valid = result != null;

      return result;
    },

    clientItems() {
      // Filter clients without children
      return this.structureItems?.filter(item => _.startsWith(item.id, 'C'))
        .map(item => ({ id: item.id, label: item.label }));
    },

    clientItem() {
      return this.structureItems?.find(ci => ci.id === this.clientValue);
    },

    surveyItems() {
      let result = this.clientItem?.children?.filter(item => _.startsWith(item.id, 'P'));

      // If only one project exists then no project node is returned by backend, so take the surveys directly
      if (result.length == 0)
        result = this.clientItem?.children?.filter(item => _.startsWith(item.id, 'S'));

      return result;
    },
  },

  activated() {
    this.getSurveyStructure();
    this.getClientSettings();
    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  deactivated() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  beforeRouteLeave (to, from, next) {
    this.$emit('unsavedChanges', this.isFormChanged, next);
  },

  methods: {
    setLogoUrl(value) {
      if (!value) {
        this.clientSettings.LogoUrl = null;
      }
    },

    getLogoUrl() {
      return this.clientSettings.LogoUrl ? this.clientSettings.LogoUrl : null;
    },

    localizationChanged(localizationId) {
      this.getSurveyStructure();
    },

    // Get survey structure data for the structure treeselect
    getSurveyStructure() {
      SurveysService.getSurveyStructure()
        .then(response => {
          this.structureItems = response.data;

          if (!this.clientValue)
          {
            let firstClient = _.first(this.clientItems);
            this.clientInput(firstClient ? firstClient.id : null);
          }
        });
    },

    getClientSettings() {
      if (this.clientValue)
      {
        Promise.all([
          AdministationService.getThemes(), 
          AdministationService.getClientSettings(this.clientValue.substring(1))])
          .then(([responseThemes, responseClientSettings]) => {
            this.isBusy = false;

            this.themes = responseThemes.data;

            this.clientSettings = responseClientSettings.data ? responseClientSettings.data[0] : null;
            if (this.clientSettings)
            {
              this.clientSettings.LogoUrl = 
                this.clientSettings.LogoExternalImageId ?
                  process.env.BASE_URL + `api/media/image?id=${this.clientSettings.LogoExternalImageId}` :
                  process.env.BASE_URL + 'static/img/msm_loop_logo_40px.png';
              this.themeMode = (this.selectedTheme || {}).ThemeModeId || ThemeMode.Color;
              this.mainColor = this.themeMode == ThemeMode.Color ? (this.selectedTheme || {}).MainColor : null;

              this.clientSettings.SurveyPreselection = this.clientSettings.SurveyPreselection?.map(item => "S" + item);
            }

            this.$validator.reset();
          });
      }
      else
      {
        this.clientSettings = null;
        this.$validator.reset();
      }
    },

    getSettingValue(settingName) {
      return this.clientSettings[settingName] ?? this.clientSettings.General?.[settingName];
    },

    setSettingValue(settingName, newValue) {
      this.clientSettings[settingName] = newValue;
      this.validationFields[settingName].dirty = true;
    },

    useGeneralSetting(settingName) {
      return this.clientSettings[settingName] === undefined || this.clientSettings[settingName] === null;
    },

    resetSettingValue(settingName, useGeneralSetting) {
      this.setSettingValue(settingName, useGeneralSetting ? null : _.clone(this.clientSettings.General?.[settingName]) ?? {});
    },

    themeModeChanged(themeModeValue) {
      this.themeChanged(null);
    },

    mainColorChanged(color) {
      if (this.mainColor !== color) 
      { 
        this.mainColor = color; 
        this.themeChanged(this.filteredThemes[0]);
      }
    },

    themeChanged(theme) {
      let newValue = theme ? theme.ThemeId : null;

      if (this.clientSettings.ThemeId != newValue)
      {
        this.clientSettings.ThemeId = newValue
        this.$validator.flag('Theme', { dirty: true });
      }
    },

    showCheckReportPreview() {
      this.isBusy = true;

      // Open in new tab
      var win = window.open('', '_blank');
      win.document.title = this.$t('Webportal.Dialog.CheckReportPreview.Title');

      AdministationService.getCheckReportPreview(
        this.clientValue.substring(1), this.clientSettings, this.selectedTheme, this.clientSettings.Thresholds)
        .then(response => {
          this.isBusy = false;

          if (response.status === 204)
          {
            win.close();
            this.noDataAlert = 5;
          }
          else {
            const blobUrl = window.URL.createObjectURL(response.data);
            win.location.href = `${process.env.BASE_URL}static/pdfjs/web/viewer.html?file=${blobUrl}`;
            win.focus();
          }
        })
        .catch(_ => { 
          win.close();
          this.isBusy = false; 
          });
    },

    showCheckReportFileNamePreview() {
      this.isBusy = true;
      AdministationService.getCheckReportFileNameSamples(this.clientValue.substring(1), this.clientSettings.CheckReportFileNameTemplate)
        .then(response => {
          this.isBusy = false;

          this.checkReportFileNameSamples = response.data;
          let dialog = this.$refs.checkReportFileNamePreview;
          dialog.show();
        })
        .catch(_ => { this.isBusy = false; } );
    },

    resetCheckReportFileNameTemplate() {
      this.clientSettings.CheckReportFileNameTemplate =
        '{ExternalSurveyInstanceId}_{WaveName}_{LocationIdOnClientSite}_{LocationName}{Result:_0}';
      this.validationFields['CheckReportFileNameTemplate'].dirty = true;
    },

    showCheckUploadFileNamePreview() {
      this.isBusy = true;
      AdministationService.getCheckUploadFileNameSamples(this.clientValue.substring(1), this.clientSettings.CheckUploadFileNameTemplate)
        .then(response => {
          this.isBusy = false;

          this.checkUploadFileNameSamples = response.data;
          let dialog = this.$refs.checkUploadFileNamePreview;
          dialog.show();
        })
        .catch(_ => { this.isBusy = false; } );
    },

    resetCheckUploadFileNameTemplate() {
      this.clientSettings.CheckUploadFileNameTemplate =
        '{ExternalSurveyInstanceId}_{LocationIdOnClientSite}\\{QuestionTextShort}_{UploadFileName}';
      this.validationFields['CheckUploadFileNameTemplate'].dirty = true;
    },

    showSurveyRevisionOnlyQuestionsWithScoringChangeMsgBox() {
      this.$bvModal.msgBoxOk(
        this.$t('Webportal.Dialog.SurveyRevisionOnlyQuestionsWithScoring'),
        {
            title: this.$t('Webportal.Dialog.SurveyRevisionOnlyQuestionsWithScoring.Title'),
            okTitle: this.$t("Webportal.Dialog.Ok"),
            hideHeaderClose: false,
        }
      );
    },

    // Called whenever the input of the structure treeselect changes
    clientInput(value) {
      if (this.clientValue !== value)
      {
        let currentValue = this.clientValue;
        this.clientValue = value;
        
        this.$emit('unsavedChanges', this.isFormChanged, result => {
          if (result === false)
          {
            this.clientValue = currentValue;
          }
          else
            this.getClientSettings();
        });
      }
    },

    cancel() {
      this.$emit('unsavedChanges', this.isFormChanged, result => {
        if (result !== false) this.getClientSettings();
      });
    },

    submit() {
      this.isBusy = true;

      // Clone settings for submission
      let clientSettings = { ... this.clientSettings };

      clientSettings.SurveyPreselection = this.clientSettings.SurveyPreselection?.map(item => item.startsWith("S") ? Number(item.substring(1)) : item);

      AdministationService.setClientSettings(clientSettings)
        .then(_ => {
          if (this.validationFields['Logo'].dirty)
            this.$refs.logoUploader.uploadImage();

          this.isBusy = false;
          this.$validator.reset();
        })
        .catch(_ => { this.isBusy = false; } );
    }
  }
}
</script>
